import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { TOAST } from '@/lib/toastMessages';

export interface ProjectCitation {
  id: string;
  project_id: string;
  research_item_id: string;
  section_id: string | null;
  citation_text: string | null;
  page_reference: string | null;
  is_direct_quote: boolean;
  created_at: string;
  updated_at: string;
  // Joined from research_items
  research_item?: {
    id: string;
    title: string;
    type: string;
    source_author: string | null;
    source_publication: string | null;
    source_publication_date: string | null;
    source_url: string | null;
    source_doi: string | null;
    source_isbn: string | null;
    source_page_numbers: string | null;
    is_authored_by_user: boolean | null;
  };
}

export interface CitationWithSource extends ProjectCitation {
  research_item: NonNullable<ProjectCitation['research_item']>;
}

export function useProjectCitations(projectId: string | undefined) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [citations, setCitations] = useState<CitationWithSource[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCitations = useCallback(async () => {
    if (!projectId || !user) {
      setCitations([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('project_citations')
        .select(`
          *,
          research_item:research_items (
            id,
            title,
            type,
            source_author,
            source_publication,
            source_publication_date,
            source_url,
            source_doi,
            source_isbn,
            source_page_numbers,
            is_authored_by_user
          )
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Filter out citations where research_item is null and type-cast
      const validCitations = (data || []).filter(
        (c): c is CitationWithSource => c.research_item !== null
      );

      setCitations(validCitations);
    } catch (error) {
      console.error('Error fetching citations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, user]);

  useEffect(() => {
    fetchCitations();
  }, [fetchCitations]);

  // Subscribe to realtime updates for cross-tab sync
  useEffect(() => {
    if (!projectId || !user) return;

    const channel = supabase
      .channel(`citations-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_citations',
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          fetchCitations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, user, fetchCitations]);

  const addCitation = async (
    researchItemId: string,
    sectionId?: string,
    citationText?: string,
    pageReference?: string,
    isDirectQuote?: boolean
  ) => {
    if (!projectId || !user) return null;

    try {
      const { data, error } = await supabase
        .from('project_citations')
        .upsert({
          project_id: projectId,
          research_item_id: researchItemId,
          section_id: sectionId || null,
          citation_text: citationText || null,
          page_reference: pageReference || null,
          is_direct_quote: isDirectQuote || false,
        }, {
          onConflict: 'project_id,research_item_id,section_id'
        })
        .select()
        .single();

      if (error) throw error;

      // Fetch the full citation with research_item for optimistic update
      if (data) {
        const { data: fullCitation } = await supabase
          .from('project_citations')
          .select(`
            *,
            research_item:research_items (
              id,
              title,
              type,
              source_author,
              source_publication,
              source_publication_date,
              source_url,
              source_doi,
              source_isbn,
              source_page_numbers,
              is_authored_by_user
            )
          `)
          .eq('id', data.id)
          .single();

        if (fullCitation && fullCitation.research_item) {
          setCitations(prev => [...prev, fullCitation as CitationWithSource]);
        }
      }

      return data;
    } catch (error) {
      console.error('Error adding citation:', error);
      toast({
        ...TOAST.CITATION.ADD_ERROR,
        variant: 'destructive',
      });
      return null;
    }
  };

  const removeCitation = async (citationId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('project_citations')
        .delete()
        .eq('id', citationId);

      if (error) throw error;

      setCitations(prev => prev.filter(c => c.id !== citationId));
    } catch (error) {
      console.error('Error removing citation:', error);
      toast({
        ...TOAST.CITATION.DELETE_ERROR,
        variant: 'destructive',
      });
    }
  };

  const removeCitationByResearchItem = async (researchItemId: string, sectionId?: string) => {
    if (!projectId || !user) return;

    try {
      let query = supabase
        .from('project_citations')
        .delete()
        .eq('project_id', projectId)
        .eq('research_item_id', researchItemId);

      if (sectionId) {
        query = query.eq('section_id', sectionId);
      }

      const { error } = await query;

      if (error) throw error;

      setCitations(prev => prev.filter(c => 
        !(c.research_item_id === researchItemId && 
          (sectionId ? c.section_id === sectionId : true))
      ));
    } catch (error) {
      console.error('Error removing citation:', error);
    }
  };

  const updateCitation = async (
    citationId: string,
    updates: {
      section_id?: string | null;
      citation_text?: string | null;
      page_reference?: string | null;
      is_direct_quote?: boolean;
    }
  ) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('project_citations')
        .update({
          section_id: updates.section_id,
          citation_text: updates.citation_text,
          page_reference: updates.page_reference,
          is_direct_quote: updates.is_direct_quote,
        })
        .eq('id', citationId)
        .select()
        .single();

      if (error) throw error;

      // Optimistic update - update local state immediately
      if (data) {
        setCitations(prev => prev.map(c => 
          c.id === citationId 
            ? { ...c, ...updates }
            : c
        ));
      }

      return data;
    } catch (error) {
      console.error('Error updating citation:', error);
      toast({
        ...TOAST.CITATION.UPDATE_ERROR,
        variant: 'destructive',
      });
      return null;
    }
  };

  // Get unique sources (for bibliography)
  const getUniqueSources = useCallback(() => {
    const uniqueMap = new Map<string, CitationWithSource['research_item']>();
    
    citations.forEach(citation => {
      // Skip user-authored content
      if (citation.research_item.is_authored_by_user) return;
      
      if (!uniqueMap.has(citation.research_item_id)) {
        uniqueMap.set(citation.research_item_id, citation.research_item);
      }
    });

    return Array.from(uniqueMap.values());
  }, [citations]);

  return {
    citations,
    isLoading,
    addCitation,
    removeCitation,
    removeCitationByResearchItem,
    updateCitation,
    getUniqueSources,
    refetch: fetchCitations,
  };
}
